// Utils

import classnames from 'classnames'
import { useTranslation } from 'next-i18next'

import { DateTime, STANDARD_DATE_OVERRIDES } from '@src/blocks/date-time'
import { useStatsigDynamicLocaleConfig } from '@src/experiments/statsig-integration/use-statsig-dynamic-locale-config.hook'
import { useI18nPreviewContext } from '@src/quince-site-common/src/contexts/i18n-preview-context/i18n-preview-context'
import { IReviewOption, ReviewOptionType } from '@src/quince-site-common/src/domains/ProductReview'
import useDeviceInfo, { DEVICE_TYPES } from '@src/quince-site-common/src/hooks/useDeviceInfo'
import { getSanitizedHtml } from '@src/quince-site-common/src/utils/getSanitizedHtml'
import { getDisplayKey } from '@src/utils/i18n.utils'

import Image, { ImageProps } from '../../image'
import Swiper from '../../swiper'
import ReviewMetaRow from '../reviewMetaRow/ReviewMetaRow'
import ReviewStars from '../reviewStars'
import SliderRangeItem from '../sliderRangeItem'

import styles from './reviewItem.module.scss'

// Not used for display
// @i18n-ignore
const CHILD_AGE = `Child's age`

// Safe HTML component to handle sanitized content
const SafeHTML: React.FC<{ html: string; className?: string; as?: keyof JSX.IntrinsicElements }> = ({
  html,
  className,
  as: Component = 'div',
}) => {
  return <Component className={className} dangerouslySetInnerHTML={{ __html: getSanitizedHtml(html) }} />
}

export interface ReviewItemProps {
  reviewId: number
  index?: number
  author: string
  date: string
  reviewOptions?: IReviewOption[]
  rating: number
  title: string
  body: string
  productTitle?: string
  reviewImageProps?: ImageProps[]
  className?: string
  reviewStarsWrapClassName?: string
  onReviewImageClick?: (reviewId: number, imageIndex: number) => void
  isKidsAndBabyProduct?: boolean
  source?: string
  brandName?: string
  newPdpv2?: boolean
  /** Country from RMS mapping of API regionId (e.g. "United States"). */
  reviewedInCountry?: string
  /** URL for "Learn more" in reward programme tooltip (e.g. contact-us with Gladly HC article query). When not set, falls back to default. */
  rewardProgrammeLearnMoreUrl?: string
  /** Shows reward programme indicator only when review is explicitly incentivized. */
  isIncentivized?: boolean
}

const getFormattedAuthorName = (authorName: string) => {
  const [fn, ln] = authorName.split(' ')
  return (fn ?? '') + (ln ? ` ${ln[0]}.` : '')
}

const hideChildAge = (name: string, isKidsAndBabyProduct: boolean) => {
  return name === CHILD_AGE && !isKidsAndBabyProduct
}

export const ReviewItem = ({ className = '', ...props }: ReviewItemProps) => {
  const { t } = useTranslation('common')
  const classes = classnames(styles.reviewItemWrap, className, {
    [styles.reviewItemWrap_newPdpv2 ?? '']: props.newPdpv2,
  })
  const userReviewClass = classnames(styles.reviewInner, styles.userReview)
  const rangeDivClass = classnames(styles.reviewInner, styles.rangeWrap)
  const reviewImageClass = classnames(styles.reviewInner, styles.reviewImage)
  const deviceInfo = useDeviceInfo()
  const localeConfig = useStatsigDynamicLocaleConfig()
  const isNewLocalisedFlow = useI18nPreviewContext()
  const isDesktop = deviceInfo.deviceType === DEVICE_TYPES.DESKTOP
  const reviewDateFormatName = localeConfig.displayLocalisedReviewSize
    ? ('SHORT_MONTH_DAY_YEAR' as const)
    : ('NUMERIC_SLASH_DATE' as const)

  const authorName = getFormattedAuthorName(props.author)
  return (
    <div data-testid={`review-section-review-container-${props.index}`} className={classes}>
      <div className={classnames(styles.reviewInner, styles.authorIntro)}>
        {authorName && <h2 className={styles.authorName}>{authorName}</h2>}
        {props.date && (
          <h2 data-testid={`review-section-review-item-date-${props.index}`} className={styles.reivewDate}>
            <DateTime
              date={props.date}
              formatName={reviewDateFormatName}
              overrides={STANDARD_DATE_OVERRIDES.REVIEW_ITEM}
            />
          </h2>
        )}
        <ul className={styles.productInfoList}>
          {props.reviewOptions
            ?.filter(option => option.attributeType === ReviewOptionType.CUSTOMER_PROFILE)
            .map(option => {
              if (hideChildAge(option.attributeName, !!props.isKidsAndBabyProduct)) return null
              return (
                <li key={option.attributeName}>
                  {getDisplayKey(option, 'translatedAttributeName', option.attributeName, isNewLocalisedFlow)}:{' '}
                  {getDisplayKey(option, 'translatedDisplayName', option.displayName, isNewLocalisedFlow)}
                </li>
              )
            })}
        </ul>
      </div>
      <div data-testid={`review-section-review-item-stars-${props.index}`} className={userReviewClass}>
        <ReviewStars
          totalStars={5}
          fillStars={props.rating}
          className={props.reviewStarsWrapClassName}
          classNameIcon={styles.ratingIcon}
        />
        {props.title && (
          <SafeHTML html={props.title} className={classnames('font-family--ivy-text', styles.title)} as="div" />
        )}
        <SafeHTML html={props.body} className={styles.message} as="p" />
        {props.source === 'THIRD_PARTY' && props.brandName && (
          <p className={styles.brandName}>
            {t('customer_review_from')} {props.brandName}
          </p>
        )}
        <ReviewMetaRow
          reviewedInCountry={props.reviewedInCountry}
          isIncentivized={props.isIncentivized}
          rewardProgrammeLearnMoreUrl={props.rewardProgrammeLearnMoreUrl}
        />
      </div>

      <div className={rangeDivClass}>
        {Array.isArray(props.reviewOptions) && props.reviewOptions.length > 0
          ? props.reviewOptions
              ?.filter(o => o.attributeType === ReviewOptionType.PRODUCT_PROFILE)
              .map(reviewOption => {
                return (
                  <SliderRangeItem
                    key={reviewOption.attributeName}
                    sliderPercentage={reviewOption.percentage ?? 0}
                    title={getDisplayKey(
                      reviewOption,
                      'translatedAttributeName',
                      reviewOption.attributeName,
                      isNewLocalisedFlow,
                    )}
                    value={getDisplayKey(
                      reviewOption,
                      'translatedDisplayName',
                      reviewOption.displayName,
                      isNewLocalisedFlow,
                    )}
                    className={styles.rangeItem}
                    newPdpv2={props.newPdpv2}
                  />
                )
              })
          : []}
      </div>

      {isDesktop ? (
        <div className={reviewImageClass}>
          <Swiper
            id={'review-item-carousal'}
            showNavigator={isDesktop}
            isStickyFreeMode
            numberOfVisibleSlides={1}
            isChevronIcon
            variableWidth
            withChildWrapper
            prevIconClass={styles.navIcon}
            nextIconClass={styles.navIcon}
          >
            {props.reviewImageProps?.map((imageProps, index) => (
              <div
                key={imageProps.id}
                onClick={() => {
                  props.onReviewImageClick?.(props.reviewId, index)
                }}
                style={{
                  // @i18n-ignore
                  width: '180px !important',
                }}
                className={styles.reviewImage__imageWrapper}
                data-testid={`review-section-review-item-image-${props.index}`}
              >
                <Image alt={imageProps.alt} skipSrcTransform {...imageProps} key={imageProps.title} />
              </div>
            ))}
          </Swiper>
        </div>
      ) : (
        Array.isArray(props.reviewImageProps) &&
        props.reviewImageProps.length > 0 && (
          <div className={reviewImageClass}>
            {props.reviewImageProps.map((imageProps, index) => (
              <div
                key={imageProps.id}
                onClick={() => {
                  props.onReviewImageClick?.(props.reviewId, index)
                }}
                className={styles.reviewImage__imageWrapper}
              >
                <Image alt={imageProps.alt} skipSrcTransform {...imageProps} key={imageProps.title} />
              </div>
            ))}
          </div>
        )
      )}
    </div>
  )
}

export default ReviewItem
